// Generated by tutorials/record.cjs from tutorials/articles/h18-first-day.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h18 --text-only` (wording) or
// `node tutorials/record.cjs h18` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'first-day',
  catalogId: 'h18',
  title: 'Your first day with Dietech Online',
  summary: 'New to Dietech Online? Sign in, find your way around, and see which articles to read first for your job. Plus why your menu may not match a coworker\'s, and where to get help.',
  audience: 'Everyone who is new to Dietech Online',
  area: 'general',
  learn: [
    'Sign in',
    'Find your dashboard and the menu',
    'Which articles to read first for your job',
    'Why your menu can look different from a coworker\'s',
    'Where Help is, and how to reach Dietech support',
  ],
  keywords: [
    'first day',
    'new user',
    'start here',
    'getting started',
    'sign in',
    'log in',
    'login',
    'password',
    'training',
    'onboarding',
    'role',
    'where do i start',
    'support',
    'help',
  ],
  steps: [
    {
      id: 'sign-in',
      part: 'Sign in',
      heading: 'Sign in',
      text: 'Open dietechonline.com in your web browser and select Sign In. Then enter the user name and password your facility gave you.\n\nCannot get in? The Sign In page has Start Support Session, so Dietech support can see your screen and walk you through it, and the support phone number and email address.',
      textOnly: true,
    },
    {
      id: 'dashboard',
      part: 'Sign in',
      heading: 'Your dashboard opens',
      text: 'After you sign in, your dashboard opens. It shows what changed in your facility since your last shift: new diet orders, admissions, moves, weight alerts and more. Read it at the start of every shift.\n\nYour dashboard has an article of its own: Your dashboard.',
      media: {
        image: {
          src: '/help/first-day/dashboard.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The dashboard: the current meal and census across the top, shortcuts below, then cards of recent changes.',
      },
    },
    {
      id: 'menu',
      part: 'Sign in',
      heading: 'Everything is in the menu',
      text: 'Every page is in the menu on the left. The column of icons holds the sections; select one to see its pages. You can also type a page\'s name in the search box at the top.\n\nRead Getting around Dietech Online first. It takes five minutes and makes everything else easier.',
      media: {
        image: {
          src: '/help/first-day/menu.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The column of section icons down the left side of the screen, from Home to Setup.',
      },
    },
    {
      id: 'dietary-manager',
      part: 'What to read first',
      heading: 'Dietary managers',
      text: 'Most of your day is under Residents. Read Finding a resident, Admitting a new resident, Changing a diet order, Meal patterns and Entering weights.\n\nIf your facility is connected to its EHR, read How your EHR connection works and Map EHR text to Dietech too.',
      media: {
        image: {
          src: '/help/first-day/dietary-manager.webp',
          width: 468,
          height: 858,
        },
        clip: {
          src: '/help/first-day/dietary-manager.mp4',
          width: 468,
          height: 858,
          durationSec: 4.9,
        },
        alt: 'The Residents section open in the menu, listing Residents, Census, Guests, Weight Entry, Intake Studies and Credits.',
      },
    },
    {
      id: 'dietitian',
      part: 'What to read first',
      heading: 'Dietitians',
      text: 'Read Changing a diet order and Entering weights, then look under Clinical for assessments and plans of care.\n\nAt a facility connected to its EHR, How your EHR connection works explains which change wins when your order and the EHR\'s disagree.',
      media: {
        image: {
          src: '/help/first-day/dietitian.webp',
          width: 468,
          height: 858,
        },
        clip: {
          src: '/help/first-day/dietitian.mp4',
          width: 468,
          height: 858,
          durationSec: 3.4,
        },
        alt: 'The Clinical section open in the menu, listing Assessments, Plan of Care, Plan of Care Setup and Validation.',
      },
    },
    {
      id: 'kitchen-staff',
      part: 'What to read first',
      heading: 'Kitchen staff',
      text: 'Read Taking menu orders with Selective Touch and Previewing and printing tray tickets. Selective Touch and Print Tray Tickets are also shortcuts on the dashboard.',
      media: {
        image: {
          src: '/help/first-day/kitchen-staff.webp',
          width: 468,
          height: 858,
        },
        clip: {
          src: '/help/first-day/kitchen-staff.mp4',
          width: 468,
          height: 858,
          durationSec: 3.1,
        },
        alt: 'The Kitchen section open in the menu, listing Bulk Order, Dining Rooms and Workflows.',
      },
    },
    {
      id: 'administrator',
      part: 'What to read first',
      heading: 'Administrators',
      text: 'Setup holds your facility\'s settings, units and rooms, printers and print schedules, and the roles that decide what each person can see. Read Your settings and preferences and Running a report.\n\nFor help setting up users and roles, call Dietech support.',
      media: {
        image: {
          src: '/help/first-day/administrator.webp',
          width: 468,
          height: 858,
        },
        clip: {
          src: '/help/first-day/administrator.mp4',
          width: 468,
          height: 858,
          durationSec: 3.1,
        },
        alt: 'The Setup section open in the menu, listing Facility Settings, Facilities, Unit Rooms, Printers, Print Schedules, Roles and My Settings.',
      },
    },
    {
      id: 'menu-different',
      part: 'Why your menu looks different',
      heading: 'Your menu may not match a coworker\'s',
      text: 'The menu lists only the pages your role allows, so a cook and a dietitian see different pages. Your facility\'s setup matters too: a page it does not use may be left out, or greyed out with the reason when you point to it.\n\nSelect your name at the top right to see your role and facility. If a page you need for your job is missing, ask your administrator.',
      media: {
        image: {
          src: '/help/first-day/menu-different.webp',
          width: 500,
          height: 274,
        },
        clip: {
          src: '/help/first-day/menu-different.mp4',
          width: 500,
          height: 274,
          durationSec: 5.1,
        },
        alt: 'The menu under the user\'s name: Dana Morgan, Role Dietary Manager, Facility Maple Grove Care Center, and the user name.',
      },
    },
    {
      id: 'help',
      part: 'Help and support',
      heading: 'Help is always there',
      text: 'Select Help at the top of any page for the help about that page. Every article, this one too, is listed in Help under Help Articles, by section. Type a word in Search help to find one.',
      media: {
        image: {
          src: '/help/first-day/help.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/first-day/help.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'The Help page with Help Articles open: Search help, then the Getting Started articles.',
      },
    },
    {
      id: 'support',
      part: 'Help and support',
      heading: 'Reach Dietech support',
      text: 'In Help, Support Hours shows whether support is open now, with the phone number and email address.\n\nOn the phone with support? Select Start Support Session, the headset at the top of the screen or the words at the bottom, so they can see your screen and help you.',
      media: {
        image: {
          src: '/help/first-day/support.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/first-day/support.mp4',
          width: 1280,
          height: 748,
          durationSec: 5.8,
        },
        alt: 'The Support Hours tab in Help: Support Available, the phone number and the email address, with the pointer on the Start Support Session headset at the top.',
      },
    },
  ],
  tips: [
    'Pin the two or three pages you use every day. Getting around Dietech Online shows how.',
    'Make the text bigger in My Settings if the screen is hard to read. It changes every page, only for you.',
    'Dietech Online runs in your web browser. There is nothing to install.',
  ],
  notes: [
    'Your user name, password and role come from your facility. Ask your administrator if you cannot sign in or a page you need is missing.',
    'Every resident and facility in these pictures is invented.',
  ],
  related: [
    'getting-around',
    'your-dashboard',
    'finding-a-resident',
    'settings-and-preferences',
    'admit-resident',
    'change-diet-order',
    'meal-patterns',
    'entering-weights',
    'selective-touch',
    'tray-tickets',
    'running-a-report',
    'map-ehr-text',
    'ehr-connection',
  ],
  recorded: '2026-10-28',
};

export default article;
